// controllers/loader.ts
import { isPrinting, windowSize } from "@adornis/base/client/frontend-helpers";
import { A } from "@adornis/base/env-info";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { html, render } from "lit";
import { BehaviorSubject, combineLatest, merge, Observable, timer } from "rxjs";
import { distinctUntilChanged, filter, map, mapTo, shareReplay, startWith, tap } from "rxjs/operators";
import "../renderable";
import "./chemistry-controller";
document.querySelector("html").setAttribute("style", "height: 100vh; height: -webkit-fill-available;");
if (A.isNative) {
  Keyboard.setResizeMode({ mode: KeyboardResize.Native });
  Keyboard.setScroll({ isDisabled: true });
}
var getKeyboardListener = (whatToListenTo) => {
  return new Observable((subscriber) => {
    Keyboard.addListener(
      whatToListenTo,
      (info) => {
        subscriber.next();
      }
    );
    return () => Keyboard.removeAllListeners();
  });
};
var initialHeight;
var cordovaKeyboardShown = A.isNative && A.isIOS ? merge(
  getKeyboardListener("keyboardDidShow").pipe(mapTo(true)),
  getKeyboardListener("keyboardWillShow").pipe(mapTo(true)),
  getKeyboardListener("keyboardWillHide").pipe(mapTo(false))
).pipe(startWith(false), shareReplay(1)) : merge(
  windowSize,
  timer(500).pipe(tap(() => initialHeight = window.visualViewport?.height ?? window.innerHeight))
).pipe(
  map(() => (window.visualViewport?.height ?? window.innerHeight) !== initialHeight),
  shareReplay(1)
);
var intrinsicHeight = new BehaviorSubject(0);
combineLatest([windowSize, cordovaKeyboardShown]).pipe(
  map(
    ([_, keyboardShown]) => window.visualViewport ? window.visualViewport.height : A.isIOS && !keyboardShown ? window.screen.height : window.innerHeight
  ),
  distinctUntilChanged(),
  tap(() => !A.isNative && (document.body.parentElement.scrollTop = 0))
).subscribe(intrinsicHeight);
var Loader = class {
  constructor(host, _loaderTemplate = html` loading `) {
    this._loaderTemplate = _loaderTemplate;
    this._loaderDiv = document.createElement("div");
    this._host = host;
    host.addController(this);
    this._initLoaderDiv();
  }
  hostConnected() {
    this._startSubscriptions();
  }
  hostDisconnected() {
    this._stopSubscriptions();
  }
  _show(height) {
    this._loaderDiv.style.height = height + "px";
    this._loaderDiv.style.width = "100%";
    this._loaderDiv.style.position = "absolute";
    this._loaderDiv.style.top = "0";
    this._loaderDiv.style.left = "0";
    this._loaderDiv.style.background = "white";
    this._loaderDiv.style.opacity = "1";
    this._loaderDiv.style.pointerEvents = "unset";
    this._loaderDiv.style.display = "flex";
    this._loaderDiv.style.justifyContent = "center";
    this._loaderDiv.style.alignItems = "center";
    this._loaderDiv.style.zIndex = "1002";
  }
  _hide() {
    this._loaderDiv.style.transition = "opacity .3s ease";
    requestAnimationFrame(() => {
      this._loaderDiv.style.opacity = "0";
      this._loaderDiv.style.pointerEvents = "none";
    });
  }
  _initLoaderDiv() {
    document.body.appendChild(this._loaderDiv);
    render(this._loaderTemplate, this._loaderDiv);
    this._show(window.innerHeight);
  }
  _updateComponentHeight(height, printing) {
    this._host.style.display = "block";
    if (!printing) {
      this._host.style.height = `${height}px`;
      this._host.style.position = "fixed";
    } else {
      this._host.style.height = "unset";
      this._host.style.position = "unset";
    }
    document.body.style.height = `${height}px`;
    document.querySelector("html").style.overflow = "hidden";
    this._host.style.width = "100%";
  }
  _startSubscriptions() {
    if (!this._isPrintingSubscription)
      this._isPrintingSubscription = combineLatest([intrinsicHeight, isPrinting]).subscribe(
        ([height, printing]) => this._updateComponentHeight(height, printing)
      );
    if (!this._loadingDoneSubscription)
      this._loadingDoneSubscription = combineLatest([
        this.constructor.rxjs,
        intrinsicHeight
      ]).subscribe(([done, height]) => {
        if (done !== null && !!done)
          this._hide();
        else
          this._show(height);
      });
    if (!this._keyboardSubscription)
      this._keyboardSubscription = combineLatest([cordovaKeyboardShown, windowSize, isPrinting]).subscribe(
        ([keyboardShown]) => {
          if (keyboardShown) {
            document.body.style.position = "fixed";
            document.body.style.top = "0";
            document.body.style.bottom = "0";
            document.body.style.width = "100%";
          } else {
            document.body.style.position = "unset";
            document.body.style.top = "unset";
            document.body.style.bottom = "unset";
            document.body.style.width = "100%";
          }
        }
      );
  }
  _stopSubscriptions() {
    if (this._isPrintingSubscription) {
      this._isPrintingSubscription.unsubscribe();
      this._isPrintingSubscription = void 0;
    }
    if (this._loadingDoneSubscription) {
      this._loadingDoneSubscription.unsubscribe();
      this._loadingDoneSubscription = void 0;
    }
    if (this._keyboardSubscription) {
      this._keyboardSubscription.unsubscribe();
      this._keyboardSubscription = void 0;
    }
  }
  static addRandomStatus(message) {
    this.statusMessage = message;
    this.rxjs.next(null);
  }
  static waitFor(...task) {
    this.rxjs.next(false);
    this.taskList.push(
      ...task.map(
        (task2) => task2.subscribe ? task2.pipe(filter((x) => !!x)).toPromise() : task2
      )
    );
    requestAnimationFrame(() => {
      Promise.all(this.taskList).then((x) => this.rxjs.next(true)).catch((x) => {
        throw x;
      });
    });
  }
};
Loader.taskList = [];
Loader.rxjs = new BehaviorSubject(true);
Loader.statusMessage = "";
export {
  Loader,
  cordovaKeyboardShown,
  intrinsicHeight
};
